<script setup lang="ts">
import { computed, useAttrs, type VNode } from 'vue';
import { type NavigationFailure, RouterLink } from 'vue-router';

import { cnMerge } from '@/utils/utils.js';
import IconBoxArrowUpRight from '~icons/ion/arrow-up-right-box-outline';

import InternalLink from './InternalLink.vue';
import type { KaLinkProps } from './types';

export type { KaLinkProps } from './types';

defineOptions({
  inheritAttrs: false,
});

const slots = defineSlots<{
  left(): VNode;
  default(): VNode;
  right(): VNode;
}>();

const props = withDefaults(defineProps<KaLinkProps>(), {
  inactiveClasses: undefined,
  activeClasses: undefined,
  disabled: false,
});

const attrs = useAttrs();

const isExternalLink = computed(() => {
  if (typeof props.to !== 'string') {
    return false;
  }

  const linkUrl = new URL(props.to, window.location.origin);
  const currentUrl = new URL(window.location.href);

  return linkUrl.hostname !== currentUrl.hostname;
});

const disabled = computed(
  () => props.disabled || attrs['aria-disabled'] || attrs['aria-disabled'] === 'true',
);

// A disabled link is always removed from the tab order. Otherwise honour an explicit
// tabindex from the caller (e.g. -1 to keep a decorative link inside an aria-hidden
// subtree out of the tab order) instead of silently dropping it.
const tabindex = computed(() => {
  if (disabled.value) {
    return -1;
  }
  const raw = attrs.tabindex;
  return raw === undefined || raw === null ? undefined : Number(raw);
});

const handleExternalLinkClick = (e: MouseEvent) => {
  if (disabled.value) {
    e.preventDefault();
    return;
  }
};

const handleInternalLinkClick = (
  e: MouseEvent,
  navigate: (e?: MouseEvent) => Promise<void | NavigationFailure>,
) => {
  if (disabled.value) {
    e.preventDefault();
    return;
  }

  navigate(e);
};
</script>
<template>
  <!-- prettier-ignore -->
  <InternalLink
    v-if="isExternalLink"
    v-bind="$attrs"
    :href="(props.to as string)"
    target="_blank"
    rel="noopener noreferrer"
    :class="props.class"
    :left="props.left"
    :right="props.right"
    :danger="props.danger"
    :variant="props.variant"
    :use-focus-visible="props.useFocusVisible"
    :aria-disabled="disabled"
    :tabindex="tabindex"
    @click.stop="handleExternalLinkClick"
  >
    <template v-if="'left' in slots" #left>
      <slot name="left" />
    </template>

    <slot />
    <span class="sr-only">(opens in a new tab)</span>

    <template #right>
      <slot name="right">
        <IconBoxArrowUpRight v-if="!props.right" class="size-4" />
      </slot>
    </template>
  </InternalLink>
  <RouterLink v-else v-slot="{ isActive, href, navigate }" v-bind="props" custom>
    <InternalLink
      v-bind="$attrs"
      :href="href"
      :class="cnMerge(props.class, isActive ? props.activeClasses : props.activeClasses)"
      :left="props.left"
      :right="props.right"
      :danger="props.danger"
      :variant="props.variant"
      :aria-disabled="disabled"
      :use-focus-visible="props.useFocusVisible"
      :tabindex="tabindex"
      @click.stop="handleInternalLinkClick($event, navigate)"
    >
      <template v-for="(_, slot) in slots" #[slot]>
        <slot :name="slot" />
      </template>
    </InternalLink>
  </RouterLink>
</template>
